import React, { useEffect, useState } from "react";

declare global {
  interface Window {
    confirmable: { emitter: ConfirmableEmitter };
  }
}

type ResolveCallback<TResult> = (result: TResult) => void;

export type ConfirmableProps<TResult> = {
  onResolve: ResolveCallback<TResult>;
};

type ConfirmEvent<TResult> = {
  component: React.FC<React.PropsWithChildren<ConfirmableProps<TResult>>>;
  onResolve: ResolveCallback<TResult>;
  handled: boolean;
};

type ConfirmCallback<TResult> = (event: ConfirmEvent<TResult>) => void;

class ConfirmableEmitter {
  private callback?: ConfirmCallback<unknown>;

  on(callback: ConfirmCallback<unknown>) {
    if (this.callback) return false;
    this.callback = callback;
    return true;
  }

  off(callback: ConfirmCallback<unknown>) {
    if (this.callback !== callback) return false;
    this.callback = undefined;
    return true;
  }

  emit(event: ConfirmEvent<unknown>) {
    if (!this.callback) return false;
    this.callback(event);
    return true;
  }
}

window.confirmable = window.confirmable ?? {
  emitter: new ConfirmableEmitter(),
};

const ConfirmableContainer = () => {
  const [current, setCurrent] = useState<ConfirmEvent<unknown>>();

  useEffect(() => {
    const attached = window.confirmable.emitter.on(setCurrent);
    if (!attached) {
      console.error(
        `There is already a handler for confirm events. Make sure your app does not have multiple <${ConfirmableContainer.name}> components.`,
      );
    }

    return () => {
      window.confirmable.emitter.off(setCurrent);
    };
  }, []);

  if (!current) return null;

  const Component = current.component;

  const onResolve = (result: unknown) => {
    current.onResolve(result);
    setCurrent(undefined);
  };

  return <Component onResolve={onResolve} />;
};

function asConfirmable<TProps, TResult>(
  WrappedComponent: React.FC<
    React.PropsWithChildren<TProps & ConfirmableProps<TResult>>
  >,
  outerProps: TProps,
) {
  return (props: ConfirmableProps<TResult>) => {
    return <WrappedComponent {...outerProps} {...props} />;
  };
}

function confirm<TProps = Record<string, never>, TResult = boolean>(
  component: React.FC<
    React.PropsWithChildren<TProps & ConfirmableProps<TResult>>
  >,
  props: TProps,
) {
  return new Promise<TResult>((resolve, reject) => {
    const handled = window.confirmable.emitter.emit({
      component: asConfirmable(component, props),
      onResolve: (r) => resolve(r as TResult),
      handled: false,
    });

    if (!handled) {
      reject(
        `The confirm request was not handled. Make sure your app includes a <${ConfirmableContainer.name} /> component.`,
      );
    }
  });
}

export { ConfirmableContainer, confirm };
